import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, TrendingDown, Loader2, BarChart3, Table } from 'lucide-react';
import { useEvolution } from '@/hooks/useEvolution';
import { useDashboard } from '@/hooks/useDashboard';
import type { EvolutionBlockProps } from '@/types/dashboard';
import { Separator } from '@/components/ui/separator';

type CategorySeries = {
  id: string;
  name: string;
  color: string;
  visualKey: string;
  visualOffset: number;
};

type GeneralEvolutionPoint = {
  month: string;
  level: number;
  levelLabel: string;
  score: number;
  delta: number;
};

type CategoryEvolutionPoint = {
  id: string;
  name: string;
  level: number;
  levelLabel: string;
  score: number;
  delta: number;
  color: string;
};

type CategoryEvolutionMonth = {
  month: string;
  topCategories: CategoryEvolutionPoint[];
};

type CategoryRow = {
  month: string;
  values: Record<string, {
    level: number;
    levelLabel: string;
    score: number;
    delta: number;
    color: string;
    name: string;
  }>;
};

const getLevelColors = (levelLabel: string) => {
  switch (levelLabel) {
    case 'Iniciante':
      return 'bg-red-100 text-red-800';
    case 'Aprendiz':
      return 'bg-yellow-100 text-yellow-800';
    case 'Empreendedor':
      return 'bg-blue-100 text-blue-800';
    case 'Inovador':
      return 'bg-green-100 text-green-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const normalizeInsightLines = (insightLines: EvolutionBlockProps['insightLines']): [string, string] => {
  if (Array.isArray(insightLines)) {
    return [insightLines[0] || '', insightLines[1] || ''];
  }

  const [firstLine, secondLine] = insightLines.split('\n');
  return [firstLine || '', secondLine || ''];
};

export function EvolutionBlock({ insightLines }: EvolutionBlockProps) {
  const [showTableView, setShowTableView] = useState(false);
  const {
    currentView,
    setCurrentView,
    currentPeriod,
    setCurrentPeriod,
    filteredGeneralData,
    filteredCategoriesData,
    currentPerformance,
    isLoading,
    error,
    refetch,
    categoriesData,
  } = useEvolution();
  const { data: dashboardData } = useDashboard();

  const [l1, l2] = normalizeInsightLines(insightLines);
  const finalLevelLabels = dashboardData?.evolution?.levelLabels || ['Iniciante', 'Aprendiz', 'Empreendedor', 'Inovador'];
  const categoryMonths = useMemo(
    () => (filteredCategoriesData || []) as CategoryEvolutionMonth[],
    [filteredCategoriesData]
  );
  const generalPoints = useMemo(
    () => (filteredGeneralData || []) as GeneralEvolutionPoint[],
    [filteredGeneralData]
  );
  const categoriesPerformance = categoriesData.data?.performance || null;

  const categorySeries = useMemo<CategorySeries[]>(() => {
    const map = new Map<string, CategorySeries>();

    categoryMonths.forEach((monthData) => {
      monthData.topCategories.forEach((category) => {
        if (!map.has(category.id)) {
          map.set(category.id, {
            id: category.id,
            name: category.name,
            color: category.color,
            visualKey: '',
            visualOffset: 0,
          });
        }
      });
    });

    const items = Array.from(map.values());
    const spacing = 0.08;
    const center = (items.length - 1) / 2;

    return items.map((item, index) => ({
      ...item,
      visualKey: `${item.id}__visual`,
      visualOffset: (index - center) * spacing,
    }));
  }, [categoryMonths]);

  const transformedCategoriesData = useMemo<CategoryRow[]>(() => {
    return categoryMonths.map((monthData) => {
      const values: CategoryRow['values'] = {};

      monthData.topCategories.forEach((category) => {
        values[category.id] = {
          level: category.level,
          levelLabel: category.levelLabel,
          score: category.score,
          delta: category.delta,
          color: category.color,
          name: category.name,
        };
      });

      return {
        month: monthData.month,
        values,
      };
    });
  }, [categoryMonths]);

  const categoriesChartData = useMemo(() => {
    return transformedCategoriesData.map((monthData) => {
      const row: Record<string, string | number | Record<string, CategoryRow['values'][string]>> = {
        month: monthData.month,
        values: monthData.values,
      };

      categorySeries.forEach((series) => {
        row[series.id] = monthData.values[series.id]?.level ?? null;
        row[series.visualKey] = monthData.values[series.id]
          ? monthData.values[series.id].level + series.visualOffset
          : null;
      });

      return row;
    });
  }, [categorySeries, transformedCategoriesData]);

  const hasGeneralData = !!generalPoints.length;
  const hasCategoryData = !!categoriesChartData.length && !!categorySeries.length;
  const hasData = currentView === 'geral' ? hasGeneralData : hasCategoryData;

  const CustomTooltip = ({
    active,
    payload,
    label,
  }: {
    active?: boolean;
    payload?: Array<{ payload?: Record<string, unknown> & { values?: CategoryRow['values'] } }>;
    label?: string;
  }) => {
    if (!active || !payload?.length) {
      return null;
    }

    if (currentView === 'geral') {
      const currentItem = payload[0]?.payload;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="text-sm font-medium">{`Mes: ${label}`}</p>
          <p className="text-sm text-blue-600">{`Nivel: ${currentItem?.levelLabel || 'N/A'}`}</p>
          <p className="text-sm text-green-600">{`Pontuacao: ${currentItem?.score || 0} pts`}</p>
          <p className="text-sm text-gray-500">{`Delta: ${currentItem?.delta > 0 ? '+' : ''}${currentItem?.delta || 0}`}</p>
        </div>
      );
    }

    const values = payload[0]?.payload?.values || {};
    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
        <p className="font-medium">{`Mes: ${label}`}</p>
        {categorySeries.map((series) => {
          const category = values[series.id];
          if (!category) {
            return null;
          }

          return (
            <div key={series.id}>
              <div className="flex items-center gap-2 mt-1">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: category.color }} />
                <span className="text-sm">
                  {category.name}: {category.score} pts
                  <br />
                  {category.levelLabel} digital
                </span>
              </div>
              <Separator className="my-2" />
            </div>
          );
        })}
      </div>
    );
  };

  if (isLoading) {
    return (
      <Card className="rounded-xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-medium text-gray-900">Evolucao ao longo do tempo</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-48">
          <div className="flex items-center gap-2 text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Carregando dados de evolucao...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="rounded-xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-medium text-gray-900">Evolucao ao longo do tempo</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-48">
          <div className="text-center">
            <p className="text-red-500 mb-2">Erro ao carregar dados</p>
            <Button variant="outline" size="sm" onClick={refetch}>
              Tentar novamente
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!hasData) {
    return (
      <Card className="rounded-xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-medium text-gray-900">Evolucao ao longo do tempo</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-48">
          <div className="text-center text-gray-500">
            <p>Nao ha dados de evolucao disponiveis para o periodo selecionado.</p>
            <p className="text-sm mt-2">Realize mais diagnosticos para ver sua evolucao.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="rounded-xl">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-medium text-gray-900">Evolucao ao longo do tempo</CardTitle>
          <div className="flex items-center gap-4">
            <Select value={currentPeriod} onValueChange={(value: string) => setCurrentPeriod(value as '3m' | '6m' | '12m')}>
              <SelectTrigger className="w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="3m">3 meses</SelectItem>
                <SelectItem value="6m">6 meses</SelectItem>
                <SelectItem value="12m">12 meses</SelectItem>
              </SelectContent>
            </Select>

            <Tabs value={currentView} onValueChange={(value: string) => setCurrentView(value as 'geral' | 'categorias')}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="geral">Geral</TabsTrigger>
                <TabsTrigger value="categorias">Por categorias</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4 px-6">
        {!showTableView ? (
          <div className="h-48 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={currentView === 'geral' ? filteredGeneralData || [] : categoriesChartData}
                margin={{ top: 20, right: 40, left: 60, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="month"
                  stroke="#666"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 12 }}
                />
                <YAxis
                  domain={[0, 4]}
                  ticks={[1, 2, 3, 4]}
                  tickFormatter={(value) => finalLevelLabels[value - 1] || ''}
                  stroke="#666"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 12 }}
                  width={100}
                  tickMargin={10}
                />
                <Tooltip content={<CustomTooltip />} />

                {currentView === 'geral' ? (
                  <Line
                    type="monotone"
                    dataKey="level"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={{ fill: '#ffffff', strokeWidth: 2, r: 6 }}
                    activeDot={{ r: 8, stroke: '#3b82f6', strokeWidth: 2 }}
                  />
                ) : (
                  categorySeries.map((series) => (
                    <Line
                      key={series.id}
                      type="monotone"
                      dataKey={series.visualKey}
                      connectNulls
                      stroke={series.color}
                      strokeWidth={2}
                      dot={{ fill: '#ffffff', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, stroke: series.color, strokeWidth: 2 }}
                      name={series.name}
                    />
                  ))
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="w-full">
            {currentView === 'geral' ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 font-medium text-gray-600">Mes</th>
                      <th className="text-left py-2 font-medium text-gray-600">Nivel</th>
                      <th className="text-left py-2 font-medium text-gray-600">Pontuacao</th>
                      <th className="text-left py-2 font-medium text-gray-600">Delta</th>
                    </tr>
                  </thead>
                  <tbody>
                    {generalPoints.map((item, index) => (
                      <tr key={index} className="border-b last:border-0">
                        <td className="py-2 font-medium">{item.month}</td>
                        <td className="py-2">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getLevelColors(item.levelLabel)}`}>
                            {item.levelLabel}
                          </span>
                        </td>
                        <td className="py-2 font-medium">{item.score} pts</td>
                        <td className="py-2">
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              item.delta > 0
                                ? 'bg-green-100 text-green-800'
                                : item.delta < 0
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {item.delta > 0 ? '+' : ''}
                            {item.delta}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 font-medium text-gray-600">Mes</th>
                      {categorySeries.map((category) => (
                        <th key={category.id} className="text-left py-2 font-medium text-gray-600">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: category.color }} />
                            {category.name}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {transformedCategoriesData.map((monthData) => (
                      <tr key={monthData.month} className="border-b last:border-0">
                        <td className="py-2 font-medium">{monthData.month}</td>
                        {categorySeries.map((series) => {
                          const category = monthData.values[series.id];
                          return (
                            <td key={`${monthData.month}-${series.id}`} className="py-2">
                              {category ? (
                                <div className="space-y-1">
                                  <div className="flex items-center gap-2">
                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getLevelColors(category.levelLabel)}`}>
                                      {category.levelLabel}
                                    </span>
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {category.score} pts
                                    <span
                                      className={`ml-1 ${
                                        category.delta > 0
                                          ? 'text-green-600'
                                          : category.delta < 0
                                            ? 'text-red-600'
                                            : 'text-gray-600'
                                      }`}
                                    >
                                      ({category.delta > 0 ? '+' : ''}
                                      {category.delta})
                                    </span>
                                  </div>
                                </div>
                              ) : (
                                <span className="text-xs text-gray-400">Sem dado</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {currentView === 'categorias' && categorySeries.length > 0 && (
          <div className="flex flex-wrap gap-4 justify-center mt-2">
            {categorySeries.map((category) => (
              <div key={category.id} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: category.color }} />
                <span className="text-sm text-gray-600">{category.name}</span>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between">
          {currentView === 'geral' ? (
            <div className="flex items-center gap-2">
              {currentPerformance?.trend === 'up' ? (
                <TrendingUp className="w-4 h-4 text-green-600" />
              ) : currentPerformance?.trend === 'down' ? (
                <TrendingDown className="w-4 h-4 text-red-600" />
              ) : (
                <div className="w-4 h-4 bg-gray-400 rounded-full" />
              )}
              <span
                className={`text-sm font-medium ${
                  currentPerformance?.trend === 'up'
                    ? 'text-green-600'
                    : currentPerformance?.trend === 'down'
                      ? 'text-red-600'
                      : 'text-gray-600'
                }`}
              >
                {currentPerformance?.percentage ?? 0}%
              </span>
              {currentPerformance ? (
                <span className="text-sm font-regular text-gray-500"> no {currentPerformance.period}</span>
              ) : (
                <span className="text-sm text-gray-500">Dados nao disponiveis</span>
              )}
            </div>
          ) : (
            <div className="flex flex-wrap gap-4">
              {categoriesPerformance ? (
                categorySeries.map((series) => {
                  const perf = categoriesPerformance[series.id];
                  if (!perf) {
                    return null;
                  }

                  return (
                    <div key={series.id} className="flex items-center gap-2">
                      {perf.trend === 'up' ? (
                        <TrendingUp className="w-4 h-4 text-green-600" />
                      ) : perf.trend === 'down' ? (
                        <TrendingDown className="w-4 h-4 text-red-600" />
                      ) : (
                        <div className="w-4 h-4 bg-gray-400 rounded-full" />
                      )}
                      <span
                        className={`text-sm font-medium ${
                          perf.trend === 'up'
                            ? 'text-green-600'
                            : perf.trend === 'down'
                              ? 'text-red-600'
                              : 'text-gray-600'
                        }`}
                      >
                        {perf.percentage}%
                      </span>
                      <span className="text-sm font-regular text-gray-500">{series.name}</span>
                    </div>
                  );
                })
              ) : (
                <span className="text-sm text-gray-500">Dados nao disponiveis</span>
              )}
            </div>
          )}

          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowTableView(!showTableView)}
            className="flex items-center gap-2"
          >
            {showTableView ? (
              <>
                <BarChart3 className="w-4 h-4" />
                Ver como grafico
              </>
            ) : (
              <>
                <Table className="w-4 h-4" />
                Ver como tabela
              </>
            )}
          </Button>
        </div>

        {l1 && <p className="text-sm text-muted-foreground mt-0">{l1}</p>}
        {l2 && <p className="text-sm text-muted-foreground mt-0">{l2}</p>}
      </CardContent>
    </Card>
  );
}
